/* ============================================================================
 * places.js - optional Google Places (New) rating lookup for business pages
 * ----------------------------------------------------------------------------
 * Uses a referrer-restricted browser key (Google's supported client-side
 * model; restrict to texasbarsales.com/* + Places API (New) in the console).
 * One Text Search per profile view, session-cached per permit. Everything is
 * optional: no key, a disabled API, or a miss simply leaves the static
 * review links in place.
 * ==========================================================================*/
const KEY =
  import.meta.env?.VITE_GMAPS_KEY ||
  (typeof window !== "undefined" && window.TBS_GMAPS_KEY) ||
  "";

export const HAS_PLACES = !!KEY;

/* Address guard: only trust Google's top hit when it sits at the FILED
 * address (street number + ZIP). Filings often carry the operating entity's
 * name ("MASS HOTELS LLC") while the public venue is e.g. a Hampton Inn -
 * address agreement is what proves the match, and it also rejects a new
 * tenant elsewhere on the street or an unrelated nearby business. */
function addressMatches(b, formatted) {
  if (!formatted) return false;
  const fa = String(formatted);
  const num = String(b.addr || "").match(/\d+/)?.[0];
  const zip = String(b.zip || "").slice(0, 5);
  const numOk = num ? new RegExp(`\\b${num}\\b`).test(fa) : false;
  const zipOk = /^\d{5}$/.test(zip) ? fa.includes(zip) : false;
  return num ? numOk && (zipOk || !/^\d{5}$/.test(zip)) : zipOk;
}

/* True when the filed name and Google's listing name are materially
 * different (worth surfacing "listed on Google as …"). */
export function namesDiffer(filed, listed) {
  if (!listed) return false;
  const tok = (s) => new Set(String(s).toUpperCase().replace(/[^A-Z0-9 ]/g, " ").split(/\s+/)
    .filter((w) => w && !["LLC", "INC", "CO", "CORP", "LP", "LTD", "THE", "AND"].includes(w)));
  const a = tok(filed), c = tok(listed);
  if (!a.size || !c.size) return false;
  let shared = 0;
  for (const w of a) if (c.has(w)) shared++;
  return shared / Math.min(a.size, c.size) < 0.5;
}

export async function placeRating(b) {
  if (!KEY) return null;
  const ck = `tbs-place2:${b.permit}`;
  try { const hit = sessionStorage.getItem(ck); if (hit != null) return JSON.parse(hit); } catch {}
  const res = await fetch("https://places.googleapis.com/v1/places:searchText", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": KEY,
      "X-Goog-FieldMask": "places.id,places.displayName,places.rating,places.userRatingCount,places.formattedAddress,places.googleMapsUri",
    },
    body: JSON.stringify({ textQuery: `${b.name}, ${b.addr}, ${b.city}, TX`, pageSize: 1 }),
  });
  if (!res.ok) throw new Error(`Places ${res.status}`);
  const d = await res.json();
  const p = d.places?.[0];
  /* Reject hits that aren't at the filed address - better no rating than the
   * wrong venue's rating. */
  const out = p && addressMatches(b, p.formattedAddress)
    ? { rating: p.rating ?? null, count: p.userRatingCount ?? 0, url: p.googleMapsUri || null, name: p.displayName?.text || null }
    : null;
  try { sessionStorage.setItem(ck, JSON.stringify(out)); } catch {}
  return out;
}
